/**
 * Replay playback controller: feeds recorded frames into the live game engine.
 */

import { decodeDts, decodePointers } from "../../../lib/replay/codec";
import type { ReplayClickEvent, ReplayKeyEvent, ReplayPayloadV1 } from "../../../lib/replay/types";
import { isReplayCompatible } from "../../../lib/replay/compat";

export type ReplayPlaybackSpeed = 0.5 | 1 | 2 | 4;

export type ReplayPlaybackState = {
  active: boolean;
  payload: ReplayPayloadV1 | null;
  frameIndex: number;
  simMs: number;
  speed: ReplayPlaybackSpeed;
  finished: boolean;
  /** When seeking, skip rendering until target. */
  seekTargetMs: number | null;
  dtMsList: number[];
  pointers: Array<{ x: number; y: number }>;
  keys: ReplayKeyEvent[];
  clicks: ReplayClickEvent[];
  keyCursor: number;
  clickCursor: number;
  keysDown: Set<string>;
  needsGameRestart: boolean;
};

function emptyState(): ReplayPlaybackState {
  return {
    active: false,
    payload: null,
    frameIndex: 0,
    simMs: 0,
    speed: 1,
    finished: false,
    seekTargetMs: null,
    dtMsList: [],
    pointers: [],
    keys: [],
    clicks: [],
    keyCursor: 0,
    clickCursor: 0,
    keysDown: new Set(),
    needsGameRestart: false,
  };
}

let state: ReplayPlaybackState = emptyState();
/** Fractional frame budget so 0.5x skips every other outer rAF, 2x/4x burst immediately. */
let speedCredit = 0;

export function getReplayPlaybackState(): Readonly<ReplayPlaybackState> {
  return state;
}

export function resetReplaySpeedCredit(): void {
  speedCredit = 0;
}

/**
 * How many recorded frames to process on this outer animation frame.
 * Accumulates fractional speed (0.5 → alternate 0/1; 2 → 2; 4 → 4).
 */
export function takeReplayFrameStepsThisTick(): number {
  if (!state.active || state.finished) return 0;
  if (state.seekTargetMs != null) return 1;
  speedCredit += state.speed;
  let steps = Math.floor(speedCredit);
  if (steps > 8) {
    speedCredit -= steps - 8;
    steps = 8;
  } else {
    speedCredit -= steps;
  }
  return Math.max(0, steps);
}

export function isReplayPlaybackActive(): boolean {
  return state.active;
}

export function isReplayPlaybackFinished(): boolean {
  return state.active && state.finished;
}

export function canPlayReplayPayload(payload: ReplayPayloadV1 | null | undefined): boolean {
  if (!payload) return false;
  return isReplayCompatible(payload.mode, payload.gameVersion);
}

export function beginReplayPlayback(payload: ReplayPayloadV1): boolean {
  if (!canPlayReplayPayload(payload)) return false;
  let dtMsList: number[];
  let pointers: Array<{ x: number; y: number }>;
  try {
    dtMsList = decodeDts(payload.dtsB64);
    pointers = decodePointers(payload.ptrB64);
  } catch {
    return false;
  }
  if (dtMsList.length < 1 || dtMsList.length !== pointers.length) return false;

  state = {
    active: true,
    payload,
    frameIndex: 0,
    simMs: 0,
    speed: 1,
    finished: false,
    seekTargetMs: null,
    dtMsList,
    pointers,
    keys: payload.keys ? payload.keys.slice() : [],
    clicks: payload.clicks ? payload.clicks.slice() : [],
    keyCursor: 0,
    clickCursor: 0,
    keysDown: new Set(),
    needsGameRestart: false,
  };
  speedCredit = 0;
  return true;
}

export function endReplayPlayback(): void {
  state = emptyState();
  speedCredit = 0;
}

/** Stop advancing frames (e.g. death freeze) while keeping the viewer active. */
export function freezeReplayPlayback(): void {
  if (!state.active) return;
  state.finished = true;
  state.seekTargetMs = null;
  speedCredit = 0;
}

export function setReplayPlaybackSpeed(speed: ReplayPlaybackSpeed): void {
  if (!state.active) return;
  state.speed = speed;
  // Apply immediately: drop leftover fractional credit so the next tick uses the new rate.
  speedCredit = 0;
}

export function getReplayPlaybackSpeed(): ReplayPlaybackSpeed {
  return state.speed;
}

export function requestReplaySeek(targetMs: number): void {
  if (!state.active || !state.payload) return;
  const clamped = Math.max(0, Math.min(state.payload.durationMs, Math.floor(targetMs)));
  state.finished = false;
  // Backward (or equal) seeks require a full restart from frame 0.
  if (clamped < state.simMs - 16) {
    state.frameIndex = 0;
    state.simMs = 0;
    state.keyCursor = 0;
    state.clickCursor = 0;
    state.keysDown.clear();
    state.seekTargetMs = clamped;
    state.needsGameRestart = true;
    speedCredit = 0;
    return;
  }
  state.seekTargetMs = clamped;
  state.needsGameRestart = false;
}

export function consumeReplayNeedsGameRestart(): boolean {
  if (!state.needsGameRestart) return false;
  state.needsGameRestart = false;
  return true;
}

export function clearReplaySeekTarget(): void {
  state.seekTargetMs = null;
}

export function resetReplayPlaybackToStart(): void {
  if (!state.active) return;
  state.frameIndex = 0;
  state.simMs = 0;
  state.finished = false;
  state.keyCursor = 0;
  state.clickCursor = 0;
  state.keysDown.clear();
  state.seekTargetMs = null;
}

/**
 * Advance one recorded frame. Returns dt seconds to feed the sim, or null if finished.
 * Applies pointer into `out` (normalized→canvas) and mutates `keys` map for WASD.
 */
export function stepReplayPlaybackFrame(
  out: { x: number; y: number },
  keys: Record<string, boolean>,
  canvasW: number,
  canvasH: number,
): number | null {
  if (!state.active || state.finished) return null;
  if (state.frameIndex >= state.dtMsList.length) {
    state.finished = true;
    return null;
  }

  const dtMs = state.dtMsList[state.frameIndex] || 16;
  const ptr = state.pointers[state.frameIndex] || { x: 0.5, y: 0.5 };
  out.x = ptr.x * canvasW;
  out.y = ptr.y * canvasH;

  const nextSim = state.simMs + dtMs;
  while (state.keyCursor < state.keys.length) {
    const ev = state.keys[state.keyCursor]!;
    if (ev.t > nextSim) break;
    if (ev.down) state.keysDown.add(ev.key);
    else state.keysDown.delete(ev.key);
    state.keyCursor++;
  }
  for (const k of Object.keys(keys)) keys[k] = false;
  for (const k of state.keysDown) keys[k] = true;

  state.simMs = nextSim;
  state.frameIndex++;

  if (state.frameIndex >= state.dtMsList.length) {
    state.finished = true;
  }

  return dtMs / 1000;
}

/** Peek clicks whose timestamp is within the last stepped frame window. */
export function drainReplayClicksSince(prevSimMs: number): ReplayClickEvent[] {
  if (!state.active) return [];
  const out: ReplayClickEvent[] = [];
  while (state.clickCursor < state.clicks.length) {
    const c = state.clicks[state.clickCursor]!;
    if (c.t < prevSimMs) {
      state.clickCursor++;
      continue;
    }
    if (c.t > state.simMs) break;
    out.push(c);
    state.clickCursor++;
  }
  return out;
}

export function getReplayProgress01(): number {
  if (!state.active || !state.payload || state.payload.durationMs <= 0) return 0;
  return Math.max(0, Math.min(1, state.simMs / state.payload.durationMs));
}

export function getReplayDurationMs(): number {
  return state.payload?.durationMs ?? 0;
}

export function getReplaySimMs(): number {
  return state.simMs;
}

/** How many frames to fast-forward without painting when seeking. */
export function replaySeekFramesBudget(): number {
  return state.seekTargetMs != null ? 4000 : 0;
}

export function isReplaySeeking(): boolean {
  return state.seekTargetMs != null;
}

export function replaySeekReached(): boolean {
  if (state.seekTargetMs == null) return true;
  return state.simMs >= state.seekTargetMs || state.finished;
}
